// ----------
// Application Pack
// ----------
//
// The Application pack is included on Rails views via <%= javascript_chunk_tag "application" %>
//
// Exposes globals according to what's needed by:
//  1. Rails Asset Pipeline code
//  2. Code in /backdraft
//  3. Code referenced on server-side rendered views
//
import "core-js/stable";
import "regenerator-runtime/runtime";

import jQuery from "jquery";
import "jquery-deparam";
import "vendor/jquery-ui";
import "vendor/jquery/cookie";
import "vendor/jquery/effect.highlight";

// this needs to come before "jquery-migrate;
import "build/application_pack/jquery_migrate_settings";
import "jquery-migrate";

import "shared/jquery/datepicker";
import "shared/jquery/multi_select";
import "shared/jquery/help_popover";
import "shared/jquery/inline_edit";
import "shared/jquery/clippy";
import "shared/jquery/icon";
import "select2";

import "vendor/bootstrap";
import "vendor/bootstrap_switch";
import "vendor/backbone/stickit";
import "vendor/jquery/textcomplete";
import "vendor/jquery/typeahead";

import _ from "underscore";
import Backbone from "backbone";
import Backdraft from "backdraft-app/src/backdraft";
import toastr from "vendor/toastr";
import React from "react";
import ReactDOM from "react-dom";
import ReactDOMServer from "react-dom/server";
import Dropzone from "vendor/dropzone";

// Backdraft components
import ActionDropdown from "shared/components/action_dropdown";
import AudioPlayerRenderer from "shared/browser_audio_player/renderer";
import CampaignSelector from "shared/components/campaign_selector";
import CampaignSelection from "shared/components/campaign_selector/campaign_selection";
import CampaignSelectorDataTable, { TableRow as CampaignSelectorTableRow } from "shared/components/campaign_selector/data_table";
import DateRangeSelector from "shared/components/date_range_selector";
import DropzoneView from "shared/components/dropzone";
import ListActionDropdown from "shared/components/list_action_dropdown";
import SignalTypeBadges from "shared/components/SignalTypeBadges";
import SubstitutionParamsPicker from "shared/components/substitution_params_picker";
import WebhookStatsPanel from "shared/components/webhook_stats_panel";
import AuthAlertView from "connect_apps/facebook_settings/views/auth_alert_view";
import EventSyncAlertView from "connect_apps/facebook_settings/views/event_sync_alert_view";
import AccountSyncAlertView from "connect_apps/facebook_settings/views/account_sync_alert_view";
import GeneralInfoPanel from "connect_apps/facebook_settings/views/general_info_panel";
import NavTabsView from "connect_apps/facebook_settings/views/nav_tabs_view";

// React components
import CampaignSelectorReact from "shared/react_components/CampaignSelector";
import MultiSelectFilter from "shared/react_components/MultiSelectFilter";
import MultiSelectFilterContainer from "shared/react_components/MultiSelectFilterContainer";
import Select2 from "shared/react_components/Select2";

// Misc
import Ajax from "shared/ajax";
import Application from "shared/application";
import CitySearch from "shared/city_search";
import Class from "legacy/jquery_class";
import Config from "legacy/config";
import CustomDataActions from "shared/custom_data_modal/actions";
import DateFormatter from "shared/formatters/dates";
import Dialog from "shared/dialog";
import ExceptionHandling from "shared/logging/exception_handling";
import Flash from "shared/flash";
import Invoca from "shared/invoca";
import * as LegacyAjax from "legacy/ajax";
import Loading from "shared/loading";
import validateEmailOn from "legacy/validations/validate_email_on";
import validatePasswordOn from "legacy/validations/validate_password_on";
import onWindowError from "shared/logging/window_error";
import Select from "shared/select";
import * as StringFormatter from "shared/formatters/strings";
import Tabs from "shared/tabs";
import Toast from "shared/toast";
import Creative from "shared/creative";

import VirtualLine from "shared/virtual_line_modal/models/virtual_line";
import NewVirtualLineModal from "shared/virtual_line_modal/views/new_virtual_line_modal";
import EditVirtualLineModal from "shared/virtual_line_modal/views/edit_virtual_line_modal";
import DeleteVirtualLineModal from "shared/virtual_line_modal/views/delete_virtual_line_modal";
import MoveVirtualLineModal from "shared/virtual_line_modal/views/move_virtual_line_modal";

import "sparks/action_table";
import "sparks/controls";
import "sparks/form";
import "sparks/integration_modals";
import "sparks/invoca_app";
import "sparks/invoca_sync";
import "sparks/modal_dialog";
import "sparks/panel";
import "sparks/renderers";
import "sparks/status_change_reason_modal";

/*********************************************************/
// Components used by /backdraft code
/*********************************************************/
const CampaignSelectorExport = CampaignSelector;
CampaignSelectorExport.CampaignSelection = CampaignSelection;
CampaignSelectorExport.TableRow = CampaignSelectorTableRow;
CampaignSelectorExport.DataTable = CampaignSelectorDataTable;

global.SharedComponents = {
  ActionDropdown,
  AudioPlayerRenderer,
  CampaignSelector: CampaignSelectorExport,
  DateRangeSelector,
  Dropzone: DropzoneView,
  ListActionDropdown,
  SignalTypeBadges,
  NewVirtualLineModal,
  EditVirtualLineModal,
  DeleteVirtualLineModal,
  MoveVirtualLineModal,
  SubstitutionParamsPicker,
  VirtualLine,
  WebhookStatsPanel
};

global.FacebookSettings = {
  AuthAlertView,
  EventSyncAlertView,
  AccountSyncAlertView,
  GeneralInfoPanel,
  NavTabsView
};

global.ReactComponents = {
  CampaignSelector: CampaignSelectorReact,
  MultiSelectFilter,
  MultiSelectFilterContainer,
  Select2
};

// This _.extend necessary because some legacy code uses spies.
global.CustomDataActions = _.extend({}, CustomDataActions);

/*********************************************************/
// Vendor globals
/*********************************************************/
global.jQuery = jQuery;
global.$ = jQuery;
global._ = _;
global.Backbone = Backbone;
global.Backdraft = Backdraft;
global.toastr = toastr;
global.React = React;
global.ReactDOM = ReactDOM;
global.ReactDOMServer = ReactDOMServer;
global.JST = {};
global.Dropzone = Dropzone;

/*********************************************************/
// Legacy globals
/*********************************************************/
global.Application = Application;
global.Application.get_csrf_token = Application.getCSRFToken;
global.Application.jquery_ajax_complete = LegacyAjax.jqueryAjaxComplete;
global.Application.ajax = LegacyAjax.ajax;

global.Dialog = Dialog;
global.CitySearch = CitySearch;
global.Class = Class;
global.Creative = Creative;

_.extend(Invoca, {
  Config,
  ExceptionHandling,
  Toast,
  Flash,
  Tabs,
  Select,
  Loading,
  Ajax,

  logError(message) {
    ExceptionHandling.logError(message);
  },

  logWarning(message) {
    ExceptionHandling.logWarning(message);
  },

  logInfo(message) {
    ExceptionHandling.logInfo(message);
  },

  log(message) {
    ExceptionHandling.log(message);
  }
});

Invoca.ExceptionHandling.onWindowError = onWindowError;

Invoca.Format = {
  Date: DateFormatter,
  String: StringFormatter
};

global.Invoca = Invoca;

/*********************************************************/
// DEPRECATED globals
// Kept for backwards compatibility
/*********************************************************/
global.validate_email_on = validateEmailOn;
global.validate_password_on = validatePasswordOn;

global.log_error = function(message) {
  ExceptionHandling.logError(message);
};

global.log_warning = function(message) {
  ExceptionHandling.logWarning(message);
};

global.log_info = function(message) {
  ExceptionHandling.logInfo(message);
};

global.log = function(message) {
  Invoca.log(message);
};

global.handle_exception = function(ex, context) {
  ExceptionHandling.handleException(ex, context);
};

// FLASH public methods - deprecated (old interface)
//  hide_flash( )
//  show_flash_notice( msg, scrollTo = true )
//  show_flash_error( msg, scrollTo = true )

global.hide_flash = function() {
  Flash.hide();
};

global.show_flash_notice = function(message, scrollTo) {
  Flash.showNotice(message, scrollTo);
};

global.show_flash_error = function(message, scrollTo) {
  Flash.showError(message, scrollTo);
};
